import { FunctionComponent, useEffect } from 'react';
import * as vp from 'vopay';
import { useTranslation } from 'react-i18next';
import { useForm } from '@/components/Form/utils';
import { Title } from 'UI';
import { Box } from 'vopay';
import { useClientData } from '@/providers/ClientData.provider';
import { useConvertMyTemplateFormFields, type ConvertMyTemplateFormValues } from './useConvertMyTemplateFormFields';

const { Card } = vp;

type ConvertMyTemplateFormProps = {
  onSubmit: (values: ConvertMyTemplateFormValues) => void;
  onValidate: (isValid: boolean) => void;
  isReviewing?: boolean;
  defaultValues?: Partial<ConvertMyTemplateFormValues>;
};

type FC = FunctionComponent<ConvertMyTemplateFormProps>;

const ConvertMyTemplateFormValues: FC = ({ onSubmit, onValidate, defaultValues = {}, isReviewing = false }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.FileUploadDrawer.ConvertTemplate' });
  const {
    response: { Country },
  } = useClientData();

  const { details, convertTo, getProductOptions, getBankOptions } = useConvertMyTemplateFormFields();

  const initialValues: ConvertMyTemplateFormValues = {
    Service: 'debit',
    Product: '',
    Currency: Country === 'US' ? 'USD' : 'CAD',
    FileBase64: '',
    FileName: '',
    Bank: '',
    ValueDate: '',
    FallbackPaymentRail: '',
    ...defaultValues,
  };

  const allFields = [...details, ...convertTo];
  const { renderFormElements, ctx } = useForm<ConvertMyTemplateFormValues>({
    initialValues,
    config: allFields,
    isReviewing,
    onSubmit: (values, ctx) => {
      onSubmit({
        ...values,
        FileName: ctx.values.FileName,
        FileBase64: ctx.values.FileBase64,
      });
    },
  });

  useEffect(() => {
    if (ctx.values.Service && ctx.values.Bank) {
      const availableProducts = getProductOptions(ctx.values.Bank, ctx.values.Service);

      if (availableProducts.length > 0) {
        const currentProductValid = availableProducts.some((p) => p.value === ctx.values.Product);
        if (!currentProductValid) {
          ctx.setFieldValue('Product', availableProducts[0].value);
        }
      } else {
        ctx.setFieldValue('Product', '');
      }
    }
  }, [ctx.values.Service, ctx.values.Bank]);

  // Handle product change - reset FallbackPaymentRail to blank
  useEffect(() => {
    if (ctx.values.Product) {
      ctx.setFieldValue('FallbackPaymentRail', '');
    }
  }, [ctx.values.Product]);

  useEffect(() => {
    onValidate(ctx.isValid);
  }, [ctx]);

  useEffect(() => {
    if (ctx.values.Bank) return;
    const availableBanks = getBankOptions();
    if (availableBanks.length > 0) {
      ctx.setFieldValue('Bank', availableBanks[0].value);
    }
  }, [getBankOptions, ctx.values.Bank]);

  return (
    <form onSubmit={ctx.handleSubmit} id="fileUploadForm">
      <Box flx="col" gap="md">
        <Card>
          <Box flx="col" gap="md">
            <Title level="5">{t('uploadTemplate')}</Title>
            {renderFormElements({ config: details })}
          </Box>
        </Card>
        <Card>
          <Box flx="col" gap="md">
            <Title level="5">{t('convertTo')}</Title>
            {renderFormElements({ config: convertTo })}
          </Box>
        </Card>
      </Box>
    </form>
  );
};

export default ConvertMyTemplateFormValues;
